import { FC } from "react"
import type {
  DashboardReporting,
  ProviderAnalyticsCollection,
  ProviderAnalyticsMetric,
} from "@/types/entities/organization"

type ProviderAnalyticsRow = {
  provider: string
  sends?: number
  replies?: number
  positiveReplies?: number
  bounces?: number
  spamTests?: SpamTestsDetails
}

type MetricKey = Exclude<keyof ProviderAnalyticsRow, "provider">
type NumericMetricKey = Exclude<MetricKey, "spamTests">

type SpamTestsDetails = {
  total?: number
  passed?: number
  failed?: number
  pending?: number
  inbox?: number
  spam?: number
  missing?: number
}

const PROVIDER_LABELS: Record<string, string> = {
  google: "Google",
  gmail: "Google",
  microsoft: "Microsoft",
  outlook: "Microsoft",
  office365: "Microsoft",
  o365: "Microsoft",
  yahoo: "Yahoo",
}

const metricLabels: Record<MetricKey, string> = {
  sends: "Sends",
  replies: "Replies",
  positiveReplies: "Positive replies",
  bounces: "Bounces",
  spamTests: "Spam tests",
}

const humanizeProvider = (provider: string) => {
  const normalized = provider.trim().toLowerCase()
  if (PROVIDER_LABELS[normalized]) return PROVIDER_LABELS[normalized]

  return provider
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const getProviderName = (
  metric: ProviderAnalyticsMetric,
  fallback?: string,
) => {
  if (metric.provider) return metric.provider
  if (metric.name) return metric.name
  if (typeof metric._id === "string") return metric._id
  if (
    metric._id &&
    typeof metric._id === "object" &&
    typeof metric._id.provider === "string"
  ) {
    return metric._id.provider
  }

  return fallback
}

const normalizeMetric = (
  value: ProviderAnalyticsMetric | number | null | undefined,
  fallbackProvider?: string,
): ProviderAnalyticsMetric | null => {
  if (value === null || value === undefined) return null
  if (typeof value === "number") {
    return {
      provider: fallbackProvider,
      total: value,
    }
  }

  return {
    ...value,
    provider: value.provider || fallbackProvider,
  }
}

const collectionEntries = (
  collection?: ProviderAnalyticsCollection,
): ProviderAnalyticsMetric[] => {
  if (!collection) return []

  if (Array.isArray(collection)) {
    return collection
      .map((item) => normalizeMetric(item))
      .filter((item): item is ProviderAnalyticsMetric => Boolean(item))
  }

  return Object.entries(collection)
    .map(([provider, item]) => normalizeMetric(item, provider))
    .filter((item): item is ProviderAnalyticsMetric => Boolean(item))
}

const numericValue = (
  metric: ProviderAnalyticsMetric,
  keys: Array<keyof ProviderAnalyticsMetric>,
) => {
  for (const key of keys) {
    const value = metric[key]
    if (typeof value === "number" && Number.isFinite(value)) {
      return value
    }
    if (typeof value === "string" && value.trim() && !Number.isNaN(+value)) {
      return +value
    }
  }

  return undefined
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const numberFromValue = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim() && !Number.isNaN(+value)) {
    return +value
  }

  return undefined
}

const numberFromKeys = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = numberFromValue(source[key])
    if (value !== undefined) return value
  }

  return undefined
}

const spamTestsDetailsFromValue = (
  value: unknown,
): SpamTestsDetails | undefined => {
  const numeric = numberFromValue(value)
  if (numeric !== undefined) return { total: numeric }

  if (!isRecord(value)) return undefined

  const details: SpamTestsDetails = {
    total: numberFromKeys(value, [
      "total",
      "spamTests",
      "spamTestsTotal",
      "spamTestCount",
      "testCount",
      "tests",
      "count",
    ]),
    passed: numberFromKeys(value, [
      "passed",
      "pass",
      "passedCount",
      "passCount",
    ]),
    failed: numberFromKeys(value, [
      "failed",
      "fail",
      "failedCount",
      "failCount",
    ]),
    pending: numberFromKeys(value, [
      "pending",
      "pendingCount",
      "queued",
      "queuedCount",
    ]),
    inbox: numberFromKeys(value, ["inbox", "inboxCount"]),
    spam: numberFromKeys(value, ["spam", "spamCount"]),
    missing: numberFromKeys(value, ["missing", "missingCount"]),
  }

  const hasDetails = [
    details.total,
    details.passed,
    details.failed,
    details.pending,
    details.inbox,
    details.spam,
    details.missing,
  ].some((item) => item !== undefined)

  if (!hasDetails) return undefined

  if (details.total === undefined) {
    details.total =
      (details.passed ?? 0) +
      (details.failed ?? 0) +
      (details.pending ?? 0) +
      (details.inbox ?? 0) +
      (details.spam ?? 0) +
      (details.missing ?? 0)
  }

  return details
}

const spamTestsDetailsFromMetric = (
  metric: ProviderAnalyticsMetric,
): SpamTestsDetails | undefined => {
  const nested = spamTestsDetailsFromValue(metric.spamTests)
  if (nested) return nested

  return spamTestsDetailsFromValue(metric)
}

const getOrCreateProviderRow = (
  rowsByProvider: Map<string, ProviderAnalyticsRow>,
  provider: string,
) => {
  const providerKey = provider.trim().toLowerCase()
  const existing = rowsByProvider.get(providerKey)
  if (existing) return existing

  const row: ProviderAnalyticsRow = {
    provider: humanizeProvider(provider),
  }
  rowsByProvider.set(providerKey, row)
  return row
}

const assignMetric = (
  rowsByProvider: Map<string, ProviderAnalyticsRow>,
  collection: ProviderAnalyticsCollection | undefined,
  metricKey: NumericMetricKey,
  keys: Array<keyof ProviderAnalyticsMetric>,
) => {
  collectionEntries(collection).forEach((metric) => {
    const provider = getProviderName(metric)
    if (!provider) return

    const value = numericValue(metric, keys)
    if (value === undefined) return

    getOrCreateProviderRow(rowsByProvider, provider)[metricKey] = value
  })
}

const assignSpamTests = (
  rowsByProvider: Map<string, ProviderAnalyticsRow>,
  collection: ProviderAnalyticsCollection | undefined,
) => {
  collectionEntries(collection).forEach((metric) => {
    const provider = getProviderName(metric)
    if (!provider) return

    const details = spamTestsDetailsFromMetric(metric)
    if (!details) return

    getOrCreateProviderRow(rowsByProvider, provider).spamTests = details
  })
}

export const getProviderAnalyticsRows = (
  reporting?: DashboardReporting,
): ProviderAnalyticsRow[] => {
  if (!reporting) return []

  const rowsByProvider = new Map<string, ProviderAnalyticsRow>()

  collectionEntries(reporting.providerSummary).forEach((metric) => {
    const provider = getProviderName(metric)
    if (!provider) return

    const row = getOrCreateProviderRow(rowsByProvider, provider)
    row.sends = numericValue(metric, ["sends", "sent", "emails", "totalSends"])
    row.replies = numericValue(metric, ["replies", "reply", "totalReplies"])
    row.positiveReplies = numericValue(metric, [
      "positiveReplies",
      "positiveReply",
      "leads",
      "lead",
    ])
    row.bounces = numericValue(metric, ["bounces", "bounce", "totalBounces"])
    row.spamTests = spamTestsDetailsFromMetric(metric)
  })

  assignMetric(rowsByProvider, reporting.providerSends, "sends", [
    "sends",
    "sent",
    "emails",
    "new",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.sendsByProvider, "sends", [
    "sends",
    "sent",
    "emails",
    "new",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.emailsByProvider, "sends", [
    "sends",
    "sent",
    "emails",
    "new",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.emails.providerTotals, "sends", [
    "sends",
    "sent",
    "emails",
    "new",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.emails.byProvider, "sends", [
    "sends",
    "sent",
    "emails",
    "new",
    "total",
  ])

  assignMetric(rowsByProvider, reporting.providerReplies, "replies", [
    "replies",
    "reply",
    "totalReplies",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.repliesByProvider, "replies", [
    "replies",
    "reply",
    "totalReplies",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.replies.providerTotals, "replies", [
    "replies",
    "reply",
    "totalReplies",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.replies.byProvider, "replies", [
    "replies",
    "reply",
    "totalReplies",
    "total",
  ])

  assignMetric(
    rowsByProvider,
    reporting.providerPositiveReplies,
    "positiveReplies",
    ["positiveReplies", "positiveReply", "leads", "lead", "total"],
  )
  assignMetric(
    rowsByProvider,
    reporting.positiveRepliesByProvider,
    "positiveReplies",
    ["positiveReplies", "positiveReply", "leads", "lead", "total"],
  )
  assignMetric(
    rowsByProvider,
    reporting.replies.providerTotals,
    "positiveReplies",
    ["positiveReplies", "positiveReply", "leads", "lead"],
  )
  assignMetric(
    rowsByProvider,
    reporting.replies.byProvider,
    "positiveReplies",
    ["positiveReplies", "positiveReply", "leads", "lead"],
  )

  assignMetric(rowsByProvider, reporting.providerBounces, "bounces", [
    "bounces",
    "bounce",
    "totalBounces",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.bouncesByProvider, "bounces", [
    "bounces",
    "bounce",
    "totalBounces",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.bounces?.providerTotals, "bounces", [
    "bounces",
    "bounce",
    "totalBounces",
    "total",
  ])
  assignMetric(rowsByProvider, reporting.bounces?.byProvider, "bounces", [
    "bounces",
    "bounce",
    "totalBounces",
    "total",
  ])

  assignSpamTests(rowsByProvider, reporting.providerSpamTests)
  assignSpamTests(rowsByProvider, reporting.spamTestsByProvider)
  assignSpamTests(rowsByProvider, reporting.spamTests?.providerTotals)
  assignSpamTests(rowsByProvider, reporting.spamTests?.byProvider)
  assignSpamTests(rowsByProvider, reporting.spamTests?.totals)
  assignSpamTests(rowsByProvider, reporting.spamTests?.data)

  return Array.from(rowsByProvider.values()).sort((a, b) =>
    a.provider.localeCompare(b.provider),
  )
}

const formatMetric = (value?: number) =>
  value === undefined ? "—" : new Intl.NumberFormat().format(value)

const formatSpamTestsDetail = (label: string, value?: number) =>
  value === undefined ? undefined : `${formatMetric(value)} ${label}`

const SpamTestsMetric: FC<{ value?: SpamTestsDetails }> = ({ value }) => {
  if (!value) return <>—</>

  const total = value.total ?? 0
  const details = [
    formatSpamTestsDetail("passed", value.passed),
    formatSpamTestsDetail("failed", value.failed),
    formatSpamTestsDetail("pending", value.pending),
    formatSpamTestsDetail("inbox", value.inbox),
    formatSpamTestsDetail("spam", value.spam),
    formatSpamTestsDetail("missing", value.missing),
  ].filter(Boolean)

  return (
    <div>
      <div>{formatMetric(total)} total</div>
      {details.length ? (
        <div className="text-xs text-text-light">{details.join(" · ")}</div>
      ) : null}
    </div>
  )
}

const ProviderAnalyticsSummary: FC<{ reporting?: DashboardReporting }> = ({
  reporting,
}) => {
  const rows = getProviderAnalyticsRows(reporting)

  if (!rows.length) return null

  return (
    <div
      className="rounded-md bg-white shadow-[0px_1px_4px_0px_rgba(0,0,0,0.08)]"
      data-testid="provider-analytics-summary"
    >
      <div className="border-b border-b-outline px-5 py-3">
        <h3 className="text-sm font-semibold">Provider analytics</h3>
        <p className="text-xs text-text-light">
          Sends, replies, positive replies, bounces, and spam tests by provider.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-xs text-text-light">
            <tr>
              <th className="px-5 py-3 text-left">Provider</th>
              {Object.values(metricLabels).map((label) => (
                <th key={label} className="px-5 py-3 text-left">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.provider} className="border-t border-t-outline">
                <td className="px-5 py-3 font-medium">{row.provider}</td>
                {(Object.keys(metricLabels) as MetricKey[]).map((key) => (
                  <td key={key} className="px-5 py-3">
                    {key === "spamTests" ? (
                      <SpamTestsMetric value={row.spamTests} />
                    ) : (
                      formatMetric(row[key])
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ProviderAnalyticsSummary
